import { DateTime } from 'luxon';
import { DateFormat } from '@equip.health/ui';

/**
 * Number of weeks to display in recurring appointments panel
 */
export const DEFAULT_NUM_WEEKS = 6;

/**
 * Selected appointment in the panel
 */
export interface SelectedAppointment {
  id: string;
  title: string;
  startTime: string;
  endTime: string;
  hostName: string;
  hostAvatar?: string;
}

/**
 * State for appointments panel selection
 */
export interface AppointmentsPanelState {
  intakeAppointment: (SelectedAppointment & { providerType: string }) | null;
  appointments: Record<string, SelectedAppointment>;
}

/**
 * Actions for appointments panel reducer
 */
export type AppointmentsPanelAction =
  | {
      type: 'SET_INTAKE_APPOINTMENT';
      payload: {
        providerType: string;
        startTime: string;
        endTime: string;
        appointmentTypeLabel: string;
        providerName: string;
        providerAvatar?: string;
      };
    }
  | {
      type: 'ADD_APPOINTMENT';
      payload: {
        id: string;
        title: string;
        startTime: string;
        endTime: string;
        hostName: string;
        hostAvatar?: string;
      };
    }
  | {
      type: 'REMOVE_APPOINTMENT';
      payload: { id: string };
    }
  | {
      type: 'SET_FOLLOW_UP_APPOINTMENTS';
      payload: { appointments: Record<string, SelectedAppointment> };
    }
  | { type: 'CLEAR_SELECTIONS' };

/**
 * Initial state for appointments panel
 */
export const initialAppointmentsPanelState: AppointmentsPanelState = {
  intakeAppointment: null,
  appointments: {},
};

/**
 * Reducer for managing selected appointments
 */
export const appointmentsPanelReducer = (
  state: AppointmentsPanelState,
  action: AppointmentsPanelAction,
): AppointmentsPanelState => {
  switch (action.type) {
    case 'SET_INTAKE_APPOINTMENT': {
      const { providerType, ...rest } = action.payload;
      const newIntakeId = `selected-intake-${providerType}-${rest.providerName}-${rest.startTime}`;
      const intakeChanged = state.intakeAppointment?.id !== newIntakeId;
      return {
        ...state,
        intakeAppointment: {
          id: newIntakeId,
          title: rest.appointmentTypeLabel,
          startTime: rest.startTime,
          endTime: rest.endTime,
          hostName: rest.providerName,
          hostAvatar: rest.providerAvatar,
          providerType,
        },
        ...(intakeChanged && { appointments: {} }),
      };
    }

    case 'ADD_APPOINTMENT': {
      const { id } = action.payload;
      return {
        ...state,
        appointments: { ...state.appointments, [id]: action.payload },
      };
    }

    case 'REMOVE_APPOINTMENT': {
      const { [action.payload.id]: _, ...rest } = state.appointments;
      return {
        ...state,
        appointments: rest,
      };
    }

    case 'SET_FOLLOW_UP_APPOINTMENTS':
      return {
        ...state,
        appointments: action.payload.appointments,
      };

    case 'CLEAR_SELECTIONS':
      return initialAppointmentsPanelState;

    default:
      return state;
  }
};

/**
 * Builds week buckets starting from the week of the admit date
 */
export const buildWeekBuckets = (
  admitDate: string,
  numWeeks: number = DEFAULT_NUM_WEEKS,
): WeekBucket[] => {
  if (!admitDate) return [];

  const admit = DateTime.fromISO(admitDate);
  if (!admit.isValid) return [];

  const weekStart = admit.startOf('week');
  const buckets: WeekBucket[] = [];

  for (let i = 0; i < numWeeks; i++) {
    const start = weekStart.plus({ weeks: i });
    const end = start.endOf('week');

    buckets.push({
      weekNumber: i + 1,
      start,
      end,
      dateRange: `${start.toFormat(DateFormat.shortMonthDayNoPad)} - ${end.toFormat(DateFormat.shortMonthDayNoPad)}`,
    });
  }

  return buckets;
};

/**
 * Groups fetched appointments into week buckets (stable — no selection dependency)
 */
export const groupAppointmentsByWeek = (
  appointments: AppointmentDetailResponse[],
  weekBuckets: WeekBucket[],
): WeekAppointments[] => {
  const grouped: WeekAppointments[] = weekBuckets.map((bucket) => ({
    weekNumber: bucket.weekNumber,
    dateRange: bucket.dateRange,
    appointments: [],
  }));

  appointments.forEach((appointment) => {
    const appointmentStart = DateTime.fromISO(
      appointment.appointmentStartDateTime,
    );

    const weekIndex = weekBuckets.findIndex(
      (bucket) =>
        appointmentStart >= bucket.start && appointmentStart <= bucket.end,
    );

    if (weekIndex !== -1) {
      grouped[weekIndex].appointments.push({
        id: appointment.appointmentExternalId,
        title: appointment.appointmentType,
        startTime: appointment.appointmentStartDateTime,
        endTime: appointment.appointmentEndDateTime,
        hostName: `${appointment.host.firstName} ${appointment.host.lastName}`,
        hostAvatar: appointment.host.avatar?.url,
        isSelected: false,
      });
    }
  });

  grouped.forEach((week) => {
    week.appointments.sort(
      (a, b) =>
        DateTime.fromISO(a.startTime).toMillis() -
        DateTime.fromISO(b.startTime).toMillis(),
    );
  });

  return grouped;
};

/**
 * Merges selection state into pre-grouped week data
 */
export const mergeSelectionsIntoWeeks = (
  groupedWeeks: WeekAppointments[],
  weekBuckets: WeekBucket[],
  selectionState: AppointmentsPanelState,
): WeekAppointments[] => {
  // Start with fetched appointments (isSelected already false)
  const merged: WeekAppointments[] = groupedWeeks.map((week) => ({
    ...week,
    appointments: [...week.appointments],
  }));

  // Collect all selected appointments to insert
  const selectedAppointments: (SelectedAppointment & { isSelected: true })[] = [];

  if (selectionState.intakeAppointment) {
    selectedAppointments.push({
      ...selectionState.intakeAppointment,
      isSelected: true,
    });
  }

  Object.values(selectionState.appointments).forEach((apt) => {
    selectedAppointments.push({
      ...apt,
      isSelected: true,
    });
  });

  // Insert each selected appointment into the correct week bucket
  selectedAppointments.forEach((selectedApt) => {
    const aptStart = DateTime.fromISO(selectedApt.startTime);
    const weekIndex = weekBuckets.findIndex(
      (bucket) => aptStart >= bucket.start && aptStart <= bucket.end,
    );

    if (weekIndex !== -1) {
      merged[weekIndex].appointments.push(selectedApt);
    }
  });

  // Re-sort each week
  merged.forEach((week) => {
    week.appointments.sort(
      (a, b) =>
        DateTime.fromISO(a.startTime).toMillis() -
        DateTime.fromISO(b.startTime).toMillis(),
    );
  });

  return merged;
};

